import React from 'react';
import { AppPagination, AppTable } from '@/components/atoms';
import type { ClinicInformationListItem } from '@/services/admin/clinicInformations.ts';
import { formatPeriodDisplay } from '@/utils/dateConverter';
import { Empty } from 'antd';
import { EMPTY_TEXT_FILTERED } from '@/constants';

type SortDir = 'ascend' | 'descend';

type SortField =
  | 'clinics.clinic_number'
  | 'clinic_informations.name'
  | 'clinic_informations.available_from';

type Meta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type ClinicInformationsTableSectionProps = {
  rows: ClinicInformationListItem[];
  loading: boolean;
  meta: Meta;
  defaultPerPage: number;
  canUseSiteTypeFeatures: boolean;
  tenantNameBySiteType: Map<string, string>;
  onSort: (field: SortField, dir: SortDir) => void;
  onChangePage: (page: number) => void;
};

const ClinicInformationsTableSection: React.FC<ClinicInformationsTableSectionProps> = ({
  rows,
  loading,
  meta,
  defaultPerPage,
  canUseSiteTypeFeatures,
  tenantNameBySiteType,
  onSort,
  onChangePage,
}) => {
  return (
    <div className="bg-white rounded-md border">
      <AppTable
        columns={[
          {
            title: '治療院ID',
            dataIndex: 'clinic_information_id',
            key: 'clinic_information_id',
            width: 140,
            sorter: true,
          },
          ...(canUseSiteTypeFeatures
            ? [
                {
                  title: 'サイト区分',
                  key: 'site_type',
                  width: 150,
                  render: (row: ClinicInformationListItem) => {
                    const siteType = row.site_type ?? undefined;
                    if (!siteType) return '';
                    return tenantNameBySiteType.get(siteType) || siteType;
                  },
                },
              ]
            : []),
          {
            title: '治療院名',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
          },
          {
            title: '有効期限',
            dataIndex: 'available_from',
            key: 'available',
            width: 260,
            sorter: true,
            render: (_: unknown, row: ClinicInformationListItem) =>
              formatPeriodDisplay(row.available_from ?? '', row.available_to ?? null),
          },
        ]}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        onChange={(_pagination, _filters, sorter) => {
          const sort = Array.isArray(sorter) ? sorter[0] : sorter;
          if (sort && sort.order) {
            const field = sort.field as string;
            let sortField: SortField = 'clinics.clinic_number';

            if (field === 'clinic_information_id') {
              sortField = 'clinics.clinic_number';
            } else if (field === 'name') {
              sortField = 'clinic_informations.name';
            } else if (field === 'available_from') {
              sortField = 'clinic_informations.available_from';
            }

            onSort(sortField, sort.order as SortDir);
          }
        }}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT_FILTERED} />
          ),
        }}
      />

      {meta && (
        <div className="p-4 flex justify-end">
          <AppPagination
            current={meta.current_page}
            total={meta.total}
            pageSize={meta.per_page || defaultPerPage}
            hideOnSinglePage
            onChange={p => onChangePage(p)}
          />
        </div>
      )}
    </div>
  );
};

export default ClinicInformationsTableSection;
